import { Slot } from 'radix-ui';
import {
  cloneElement,
  type ComponentProps,
  type ElementType,
  isValidElement,
  type ReactNode,
} from 'react';

import { tv } from '../../lib/tv';

export type LinkProps = ComponentProps<'a'> & {
  /** Figma "Tone": Brand for standalone links; Neutral in dense UI and metadata. */
  tone?: 'brand' | 'neutral';
  /** Figma "Size": Small (Body/Small Strong) · Medium (Label/Medium) · Large (Label/Large). */
  size?: 'sm' | 'md' | 'lg';
  /** Icon after the label, e.g. ArrowRight. */
  trailingIcon?: ElementType<{ className?: string }>;
  /** Keep the underline visible at rest - for links inside body copy. */
  inline?: boolean;
  /** Styles the link but makes it inert (`aria-disabled`, no navigation). */
  disabled?: boolean;
  /** Render the single child (a router Link, Next Link) with link styling. */
  asChild?: boolean;
};

const link = tv({
  slots: {
    root: [
      'group/link inline-flex items-center gap-1 rounded-xs px-0.5 outline-none',
      'transition-colors duration-[120ms] ease-out',
      // Focus: a 2px border/focus outline replaces the underline.
      'focus-visible:ring-2 focus-visible:ring-border-focus',
      'aria-disabled:pointer-events-none aria-disabled:text-text-disabled',
    ],
    // "Label stack": the label with a 1.25px underline 1px below it, in the
    // label colour, shown on hover and press.
    label: 'flex flex-col gap-px',
    underline: [
      'h-[1.25px] w-full bg-current opacity-0',
      'group-hover/link:opacity-100 group-active/link:opacity-100',
      'group-focus-visible/link:opacity-0 group-aria-disabled/link:opacity-0',
    ],
    icon: 'shrink-0',
  },
  variants: {
    tone: {
      brand: { root: 'text-text-link hover:text-text-brand active:text-text-primary' },
      neutral: {
        root: 'text-text-secondary hover:text-text-primary active:text-text-primary',
      },
    },
    size: {
      sm: { root: 'text-body-sm-strong', icon: 'size-3.5' },
      md: { root: 'text-label-md', icon: 'size-4' },
      lg: { root: 'text-label-lg', icon: 'size-[18px]' },
    },
    inline: { true: { underline: 'opacity-100' } },
  },
  defaultVariants: { tone: 'brand', size: 'md', inline: false },
});

/**
 * Text action for navigation and low-emphasis actions ("View all reels").
 * Figma: Components ↳ Button ↳ Link. Use Button when the action changes data.
 */
export function Link({
  tone,
  size,
  inline,
  trailingIcon: TrailingIcon,
  disabled,
  asChild = false,
  className,
  children,
  onClick,
  ...props
}: LinkProps) {
  const styles = link({ tone, size, inline });
  const stack = (label: ReactNode) => (
    <span className={styles.label()}>
      {label}
      <span aria-hidden className={styles.underline()} />
    </span>
  );
  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  const Comp = asChild ? Slot.Root : 'a';

  return (
    <Comp
      data-slot="link"
      className={styles.root({ className })}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      onClick={disabled ? (event) => event.preventDefault() : onClick}
      {...props}
    >
      {child ? (
        <Slot.Slottable>
          {cloneElement(child, undefined, stack(child.props.children))}
        </Slot.Slottable>
      ) : (
        stack(children)
      )}
      {TrailingIcon && <TrailingIcon aria-hidden className={styles.icon()} />}
    </Comp>
  );
}
